import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ForkKnife,
  SignIn,
  SpinnerGap
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`);
      const data = await response.json();
      
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        console.error('No authorization URL returned');
        setLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
    }
  };

  const heroImage = 'https://images.unsplash.com/photo-1644070648746-85150d916ab7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxjYXBlJTIwdG93biUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzUzMzg3NDR8MA&ixlib=rb-4.1.0&q=85';

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <SEO title="Sign In" description="Sign in to Dining Tracker — your personal dining companion for Cape Town restaurants." path="/login" />
      {/* Left side - Hero image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-heading font-bold mb-4">
            Track Your Dining Adventures
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            Discover, plan, and remember every meal in Cape Town. 
            From hidden gems to fine dining favourites.
          </p>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F9F8F5]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <ForkKnife weight="duotone" className="w-12 h-12 text-[#A94438]" />
            <div>
              <h1 className="text-2xl font-heading font-bold text-[#1A1918]">
                Dining Tracker
              </h1>
              <p className="text-sm text-[#7A7873]">Cape Town Edition</p>
            </div>
          </div>

          {/* Welcome text */}
          <div className="mb-8">
            <h2 className="text-3xl font-heading font-semibold text-[#1A1918] mb-2">
              Welcome back
            </h2>
            <p className="text-[#7A7873]">
              Sign in to continue tracking your dining experiences
            </p>
          </div>

          {/* Login button */}
          <Button
            onClick={handleLogin}
            disabled={loading}
            className="w-full btn-primary text-lg py-6"
            data-testid="sso-login-btn"
          >
            {loading ? (
              <SpinnerGap className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <SignIn weight="bold" className="w-6 h-6 mr-2" />
                Sign in with My Trackers
              </>
            )}
          </Button>

          {/* Info text */}
          <p className="text-sm text-[#7A7873] mt-6 text-center">
            Part of the My Trackers family.{' '}
            <a 
              href="https://mytrackers.co.za" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#A94438] hover:underline"
            >
              Learn more
            </a>
          </p>

          {/* Browse discovery */}
          <div className="mt-8 pt-8 border-t border-[#EAE8E3] text-center">
            <p className="text-[#7A7873] mb-3">Just browsing?</p>
            <Button
              variant="outline"
              onClick={() => navigate('/discovery')}
              className="rounded-full"
              data-testid="browse-discovery-btn"
            >
              Explore Cape Town Restaurants
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
